import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Heart } from "lucide-react";
import Petals from "../common/Petals";
import Flourish from "../common/Flourish";
import SplitText from "../common/SplitText";
import { useSmoothScroll } from "../../providers/SmoothScroll";
import { fonts, palette } from "../../theme";

const EASE = [0.16, 1, 0.3, 1] as const;

// Cible : 28 août 2026, 11h00 (mariage civil)
const TARGET = new Date(2026, 7, 28, 11, 0, 0).getTime();

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  done: boolean;
}

function computeLeft(): TimeLeft {
  const diff = TARGET - new Date().getTime();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  const s = Math.floor(diff / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
    done: false,
  };
}

// Teinte selon l'heure locale
function timeTint(): string {
  const h = new Date().getHours();
  if (h < 6 || h >= 21)
    return "linear-gradient(180deg, rgba(20,24,48,0.62), rgba(12,14,30,0.78))"; // nuit
  if (h < 11)
    return "linear-gradient(180deg, rgba(255,196,140,0.30), rgba(60,40,30,0.62))"; // matin
  if (h < 17)
    return "linear-gradient(180deg, rgba(255,240,210,0.20), rgba(50,40,30,0.55))"; // après-midi
  return "linear-gradient(180deg, rgba(214,140,70,0.34), rgba(40,26,20,0.66))"; // soir
}

// ── Styled ────────────────────────────────────────────────────

const Section = styled("section")({
  position: "relative",
  minHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  padding: "100px 20px",
  textAlign: "center",
});

const Bg = styled(motion.div)({
  position: "absolute",
  inset: "-6%",
  backgroundImage: "url('/assets/photo20.jpeg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  animation: "kenburns 26s ease-in-out infinite alternate",
  zIndex: 0,
});

const Overlay = styled(Box)({ position: "absolute", inset: 0, zIndex: 1 });
const Vignette = styled(Box)({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  background:
    "radial-gradient(120% 90% at 50% 45%, transparent 45%, rgba(0,0,0,0.45) 100%)",
});

const Content = styled(Box)({
  position: "relative",
  zIndex: 3,
  width: "100%",
  maxWidth: 1040,
  color: "#fff",
});

const Title = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "clamp(1.9rem, 5vw, 3.2rem)",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textShadow: "0 4px 30px rgba(0,0,0,0.45)",
});

const Subtitle = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
  lineHeight: 1.9,
  color: "rgba(255,255,255,0.92)",
  maxWidth: 620,
  margin: "18px auto 0",
  textShadow: "0 2px 16px rgba(0,0,0,0.4)",
});

const Grid = styled(Box)({
  position: "relative",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "20px",
  maxWidth: 720,
  margin: "44px auto 0",
  "@media (max-width:600px)": { gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" },
});

const Card = styled(Box)({
  position: "relative",
  aspectRatio: "1 / 1",
  borderRadius: 20,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.10)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(231,216,196,0.45)",
  boxShadow: "0 16px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.25)",
});

const Ring = styled("svg")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
});

const NumWrap = styled(Box)({
  height: "1.05em",
  lineHeight: 1,
  overflow: "hidden",
  fontFamily: fonts.display,
  fontWeight: 600,
  fontSize: "clamp(2.2rem, 7vw, 3.6rem)",
  color: "#fff",
});

const Label = styled(Typography)({
  fontFamily: fonts.sans,
  fontSize: "0.6rem",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: palette.champagne,
  marginTop: "10px",
});

const Quote = styled(motion.p)({
  fontFamily: fonts.serif,
  fontStyle: "italic",
  fontSize: "clamp(1.05rem, 2.4vw, 1.3rem)",
  lineHeight: 1.7,
  color: "rgba(255,255,255,0.95)",
  maxWidth: 560,
  margin: "44px auto 0",
  textShadow: "0 2px 16px rgba(0,0,0,0.4)",
});

const CTA = styled(motion.button)({
  position: "relative",
  overflow: "hidden",
  marginTop: "34px",
  padding: "14px 44px",
  borderRadius: 40,
  cursor: "pointer",
  background: "transparent",
  border: "1px solid rgba(231,216,196,0.8)",
  color: "#fff",
  fontFamily: fonts.sans,
  fontSize: "0.78rem",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  boxShadow: "0 0 0 rgba(191,160,106,0)",
  transition: "box-shadow .4s ease, background-color .4s ease",
  "&:hover": {
    backgroundColor: "rgba(231,216,196,0.12)",
    boxShadow: "0 0 26px rgba(231,216,196,0.4)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-70%",
    width: "45%",
    height: "100%",
    background:
      "linear-gradient(120deg, transparent, rgba(255,255,255,0.45), transparent)",
    transform: "skewX(-20deg)",
    transition: "left .75s ease",
  },
  "&:hover::after": { left: "130%" },
});

const Surprise = styled(motion.div)({
  fontFamily: fonts.serif,
  fontStyle: "italic",
  fontSize: "1rem",
  color: palette.champagne,
  marginTop: "22px",
  textShadow: "0 2px 12px rgba(0,0,0,0.5)",
});

// ── Carte d'une unité (chiffre + anneau + animation) ──────────

const R = 46;
const C = 2 * Math.PI * R;

function CountUnit({ value, max, label }: { value: number; max: number; label: string }) {
  const pad = String(value).padStart(2, "0");
  const frac = Math.max(0, Math.min(1, value / max));
  return (
    <Card>
      <Ring viewBox="0 0 100 100" aria-hidden>
        <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(231,216,196,0.22)" strokeWidth="2" />
        <motion.circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke="#D9BE86"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeDasharray={C}
          transform="rotate(-90 50 50)"
          animate={{ strokeDashoffset: C * (1 - frac) }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </Ring>
      <NumWrap>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={pad}
            initial={{ y: "-75%", opacity: 0, scale: 0.7, filter: "blur(4px)" }}
            animate={{
              y: 0,
              opacity: 1,
              scale: 1,
              filter: "blur(0px)",
              textShadow: [
                "0 0 22px rgba(217,190,134,0.9)",
                "0 0 0px rgba(217,190,134,0)",
              ],
            }}
            exit={{ y: "75%", opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.55, ease: EASE }}
            style={{ display: "inline-block" }}
          >
            {pad}
          </motion.span>
        </AnimatePresence>
      </NumWrap>
      <Label>{label}</Label>
    </Card>
  );
}

// ── Composant ─────────────────────────────────────────────────

export default function Countdown() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const scrollTo = useSmoothScroll();
  const sectionRef = useRef<HTMLElement>(null);
  const surpriseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [left, setLeft] = useState<TimeLeft>(computeLeft);
  const [showSurprise, setShowSurprise] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setLeft(computeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  // Parallaxe souris
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const bgX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const bgY = useTransform(sy, [-0.5, 0.5], [-16, 16]);

  const onMouseMove = (e: React.MouseEvent) => {
    if (reduce || !sectionRef.current) return;
    const r = sectionRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const armSurprise = () => {
    if (reduce) return;
    surpriseTimer.current = setTimeout(() => setShowSurprise(true), 2200);
  };
  const disarmSurprise = () => clearTimeout(surpriseTimer.current);

  const units = [
    { value: left.days, max: 365, label: t("countdown.days") },
    { value: left.hours, max: 24, label: t("countdown.hours") },
    { value: left.minutes, max: 60, label: t("countdown.minutes") },
    { value: left.seconds, max: 60, label: t("countdown.seconds") },
  ];

  return (
    <Section ref={sectionRef} onMouseMove={onMouseMove}>
      <Bg style={reduce ? undefined : { x: bgX, y: bgY }} />
      <Overlay sx={{ background: timeTint() }} />
      <Vignette />

      {/* Pétales / poussière dorée */}
      <Petals count={12} opacity={0.5} />

      {/* Balayage lumineux (toutes ~15 s) */}
      {!reduce && (
        <motion.div
          aria-hidden
          initial={{ x: "-60%" }}
          animate={{ x: "260%" }}
          transition={{ duration: 3, repeat: Infinity, repeatDelay: 12, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "35%",
            zIndex: 2,
            background:
              "linear-gradient(115deg, transparent, rgba(231,216,196,0.22), transparent)",
            filter: "blur(8px)",
            transform: "skewX(-12deg)",
          }}
        />
      )}

      <Content>
        {left.done ? (
          // ── Finale ──
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: EASE }}
          >
            <Petals count={26} opacity={0.85} />
            <Box sx={{ display: "inline-flex", color: palette.gold, mb: 2, animation: "heartbeat 2.4s ease-in-out infinite" }}>
              <Heart size={48} fill={palette.gold} strokeWidth={0.6} />
            </Box>
            <Title sx={{ fontFamily: fonts.script, fontSize: "clamp(2.4rem,7vw,4.2rem)" }}>
              {t("countdown.finale")}
            </Title>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, ease: EASE }}
            >
              <Title>
                <SplitText text={t("countdown.title")} stagger={0.04} />
              </Title>
              <Subtitle>{t("countdown.subtitle")}</Subtitle>
            </motion.div>

            {/* Cœur qui bat derrière le compte à rebours */}
            <Box
              aria-hidden
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "44px",
                  color: palette.gold,
                  opacity: 0.12,
                  animation: "heartbeat 2.6s ease-in-out infinite",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              >
                <Heart size={300} fill={palette.gold} strokeWidth={0} />
              </Box>

              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                style={{ width: "100%" }}
                onMouseEnter={armSurprise}
                onMouseLeave={disarmSurprise}
                onClick={() => setShowSurprise((s) => !s)}
              >
                <Grid>
                  {units.map((u) => (
                    <CountUnit key={u.label} value={u.value} max={u.max} label={u.label} />
                  ))}
                </Grid>
              </motion.div>
            </Box>

            {/* Surprise cachée */}
            <AnimatePresence>
              {showSurprise && (
                <Surprise
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                >
                  ❝ {t("countdown.surprise")} ❞
                </Surprise>
              )}
            </AnimatePresence>

            <Quote
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.4, ease: EASE }}
            >
              {t("countdown.quote")}
            </Quote>

            <CTA
              type="button"
              onClick={() => scrollTo("#rsvp")}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
            >
              {t("countdown.cta")}
            </CTA>
          </>
        )}
      </Content>

      {/* Ornements floraux */}
      <Box sx={{ position: "absolute", top: 30, left: 0, right: 0, zIndex: 3, opacity: 0.7 }}>
        <Flourish />
      </Box>
      <Box sx={{ position: "absolute", bottom: 30, left: 0, right: 0, zIndex: 3, opacity: 0.7 }}>
        <Flourish />
      </Box>
    </Section>
  );
}
